import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, Loader2, Mic, MicOff } from "lucide-react";
import { CameraCoachBar } from "@/components/CameraCoachBar";
import { toast } from "sonner";
import { capturePhotoBlob, openPhotoCameraStream } from "@/lib/photoCamera";
import { getCameraCoachUi, runLivePhotoCheck } from "@/lib/cameraLiveCheck";

/** Minimál kamera: előnézet + exponáló + bezárás. Opcionális hang („kész” / „következő”). */
export function CameraCapture({ open, onClose, onCapture, nextAngle, onSkipAngle }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return localStorage.getItem("autopark_cam_voice") === "1"; } catch (_) { return false; }
  });
  const [voiceListening, setVoiceListening] = useState(false);
  const recogRef = useRef(null);
  const captureRef = useRef(null);
  const coachBusyRef = useRef(false);
  const coachTimerRef = useRef(null);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachOffline, setCoachOffline] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startStream = useCallback(async () => {
    setError("");
    setErrorCode("");
    setReady(false);
    stopStream();
    try {
      const stream = await openPhotoCameraStream();
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        v.onloadedmetadata = () => setReady(true);
        await v.play?.().catch(() => {});
      }
    } catch (e) {
      const name = e?.name || "";
      setErrorCode(name);
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("A kamera hozzáférés le van tiltva.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setError("Nem található kamera (pl. telefon USB-n — használd a natív „Fotó” gombot).");
      } else {
        setError("A kamera nem indítható.");
      }
    }
  }, [stopStream]);

  useEffect(() => {
    if (!open) return;
    startStream();
    return () => {
      stopStream();
      setReady(false);
      setCoach(null);
      setCoachOffline(false);
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    };
  }, [open, startStream, stopStream]);

  const runCoach = useCallback(async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || coachBusyRef.current || !open) return;
    coachBusyRef.current = true;
    setCoachLoading(true);
    try {
      const data = await runLivePhotoCheck(video, { angle: nextAngle?.key });
      setCoach(data);
      setCoachOffline(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setCoachOffline(true);
        setCoach({
          fit: "adjust",
          advice: "Jelentkezz be az AI ellenőrzéshez",
          can_shoot: true,
          lighting_message: "",
        });
      } else {
        setCoachOffline(true);
        setCoach(null);
      }
    } finally {
      coachBusyRef.current = false;
      setCoachLoading(false);
    }
  }, [open, nextAngle?.key]);

  useEffect(() => {
    if (!open || !ready || error) return;
    runCoach();
    coachTimerRef.current = setInterval(runCoach, 2800);
    return () => {
      if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    };
  }, [open, ready, error, runCoach]);

  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startVoice = () => {
    if (!SR || recogRef.current) return;
    try {
      const rec = new SR();
      rec.lang = "hu-HU";
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (ev) => {
        const last = ev.results[ev.results.length - 1];
        const txt = (last?.[0]?.transcript || "").toLowerCase();
        if (!last.isFinal) return;
        if (/\b(k[oó]vetkez[oő]|skip|ugor[jd])\b/.test(txt)) {
          if (nextAngle && typeof onSkipAngle === "function") onSkipAngle();
        } else if (/\b(k[eé]sz|csattints|k[aá]ttints)\b/.test(txt)) {
          captureRef.current?.();
        }
      };
      rec.onerror = () => setVoiceListening(false);
      rec.onend = () => {
        try {
          if (localStorage.getItem("autopark_cam_voice") === "1" && recogRef.current) rec.start();
        } catch (_) {
          setVoiceListening(false);
        }
      };
      recogRef.current = rec;
      rec.start();
      setVoiceListening(true);
    } catch (_) {
      setVoiceListening(false);
    }
  };

  const stopVoice = () => {
    try { recogRef.current?.stop(); } catch (_) {}
    recogRef.current = null;
    setVoiceListening(false);
  };

  const toggleVoice = () => {
    setVoiceEnabled((v) => {
      const nv = !v;
      try { localStorage.setItem("autopark_cam_voice", nv ? "1" : "0"); } catch (_) {}
      if (nv) startVoice(); else stopVoice();
      return nv;
    });
  };

  useEffect(() => {
    if (!open) {
      stopVoice();
      return;
    }
    if (voiceEnabled && SR && !recogRef.current) startVoice();
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, voiceEnabled]);

  const canShoot = coach?.can_shoot !== false;

  const capture = async () => {
    if (!videoRef.current || !ready || !streamRef.current || !canShoot) return;
    setCapturing(true);
    try {
      const blob = await capturePhotoBlob(streamRef.current, videoRef.current);
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
      await onCapture(file);
      try { if (navigator.vibrate) navigator.vibrate(60); } catch (_) {}
    } catch (_) {
      toast.error("A fotó rögzítése sikertelen.");
    } finally {
      setCapturing(false);
    }
  };
  captureRef.current = capture;

  if (!open) return null;

  const permissionDenied = errorCode === "NotAllowedError" || errorCode === "PermissionDeniedError";

  const coachBanner = getCameraCoachUi(coach, {
    loading: coachLoading,
    offline: coachOffline,
    angleLabel: nextAngle?.label,
  });

  return (
    <div data-testid="camera-capture-modal" className="fixed inset-0 z-[100] bg-black">
      {error ? (
        <div
          data-testid="camera-error"
          className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-slate-200 z-10 gap-4"
        >
          <p className="text-lg font-medium text-white">{error}</p>
          {permissionDenied && (
            <ol className="text-sm text-slate-400 max-w-md text-left list-decimal list-inside space-y-2">
              <li>Kattints a címsor melletti lakat / kamera ikonra.</li>
              <li>Állítsd a Kamerát <b className="text-white">Engedélyezés</b>re.</li>
              <li>Kattints az oldal <b className="text-white">Újratöltés</b> gombjára (vagy F5).</li>
            </ol>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              data-testid="camera-retry"
              onClick={() => startStream()}
              className="px-5 py-2.5 rounded-full bg-electric text-white text-sm font-medium"
            >
              Újrapróbálás
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-full bg-white/10 text-white text-sm"
            >
              Bezárás
            </button>
          </div>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            data-testid="camera-video"
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-contain"
          />
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </>
      )}

      {!error && (
        <>
          <button
            type="button"
            data-testid="camera-close"
            onClick={onClose}
            className="absolute top-4 right-4 z-30 w-9 h-9 rounded-lg bg-white/[0.06] border border-white/10 text-slate-300 hover:text-white flex items-center justify-center"
            aria-label="Bezárás"
          >
            <X className="w-4 h-4" />
          </button>

          {coachBanner && (
            <CameraCoachBar ui={coachBanner} className="absolute top-4 left-4 right-4 z-20 pr-12" />
          )}

          <div className="absolute bottom-0 inset-x-0 z-20 pb-8 pt-4 flex items-center justify-center gap-8 bg-gradient-to-t from-black/70 to-transparent">
            {SR && (
              <button
                type="button"
                data-testid="camera-voice-toggle"
                onClick={toggleVoice}
                aria-label={voiceEnabled ? "Hangvezérlés ki" : "Hangvezérlés be"}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  voiceEnabled
                    ? voiceListening
                      ? "bg-emerald-500/30 text-emerald-200 ring-2 ring-emerald-400/50"
                      : "bg-white/20 text-white"
                    : "bg-white/10 text-white/70"
                }`}
              >
                {voiceEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
              </button>
            )}
            <button
              type="button"
              data-testid="camera-shutter"
              onClick={capture}
              disabled={!ready || capturing || !canShoot}
              title={!canShoot ? (coach?.lighting_message || "Túl árnyékos") : undefined}
              className="w-[72px] h-[72px] rounded-full bg-white disabled:opacity-40 flex items-center justify-center ring-4 ring-white/30"
              aria-label="Fotó"
            >
              {capturing ? (
                <Loader2 className="w-7 h-7 animate-spin text-black" />
              ) : (
                <span className="w-[62px] h-[62px] rounded-full border-2 border-black/10 bg-white" />
              )}
            </button>
            {SR && <span className="w-12" aria-hidden />}
          </div>

          {voiceEnabled && voiceListening && (
            <div
              data-testid="camera-voice-listening"
              className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-black/60 text-white/90 text-xs"
            >
              Mondd: kész
            </div>
          )}
        </>
      )}
    </div>
  );
}
